import DeckGL from '@deck.gl/react'
import { OrthographicView, type Layer, type PickingInfo } from '@deck.gl/core'
import { HeatmapLayer } from '@deck.gl/aggregation-layers'
import { PathLayer, ScatterplotLayer } from '@deck.gl/layers'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, getReports, getRoadnet, getSnapshot, reportUrl, startLive, type ReportItem } from '../api'
import type {
  AdvancedParams,
  IntensityLevel,
  HotspotOverlay,
  ExperimentInfo,
  Passenger,
  Roadnet,
  SliceLevel,
  Snapshot,
  StartParams,
  Vehicle,
  VehicleStatus,
} from '../types'
import {
  AVERAGE_SERIES,
  COUNT_SERIES,
  TREND_BUCKET_S,
  fitViewState,
  formatClock,
  pollPlan,
  sliceWindow,
  SPEED_GEARS,
  type TrendSample,
} from './live-utils'
import { StatChart } from './StatChart'

const DEFAULT_ADVANCED: AdvancedParams = {
  cancel_after_s: 120,
  time_diversion: {
    enabled: false,
    slices: [
      { demand: 'base', supply: 'base' },
      { demand: 'base', supply: 'base' },
      { demand: 'base', supply: 'base' },
    ],
  },
  hotspot: { origin: false, destination: false, intensity: 'medium', guide_idle: true },
  pricing: { base_fare: 8, base_distance_km: 2, per_km: 2, per_minute_low_speed: 0, low_speed_threshold_mps: 3, platform_share: 0.2 },
}

const DEMAND_LEVEL_LABEL: Record<SliceLevel, string> = { low: '低峰 0.3×', base: '基准 1×', peak: '高峰 3×' }
const SUPPLY_LEVEL_LABEL: Record<SliceLevel, string> = { low: '低峰 半数下线', base: '基准', peak: '高峰 车队翻倍' }
const INTENSITY_LABEL: Record<IntensityLevel, string> = { low: '低 · 约 20%', medium: '中 · 约 40%', high: '高 · 约 60%' }

function randomLevel(): SliceLevel {
  const levels: SliceLevel[] = ['low', 'base', 'peak']
  return levels[Math.floor(Math.random() * levels.length)]
}

// 每次点击随机生成一组分流参数：三个时间片随机供需档位，热区开关与强度随机（至少一个热区）
function randomDiversion(): AdvancedParams {
  const intensities: IntensityLevel[] = ['low', 'medium', 'high']
  let origin = Math.random() < 0.7
  const destination = Math.random() < 0.7
  if (!origin && !destination) origin = true
  return {
    cancel_after_s: 120,
    time_diversion: {
      enabled: true,
      slices: [0, 1, 2].map(() => ({ demand: randomLevel(), supply: randomLevel() })),
    },
    hotspot: {
      origin,
      destination,
      intensity: intensities[Math.floor(Math.random() * intensities.length)],
      guide_idle: true,
    },
    pricing: DEFAULT_ADVANCED.pricing,
  }
}

const DEFAULT_PARAMS: StartParams = {
  map: 'grid50',
  algorithm: 'nearest',
  vehicle_count: 50,
  passenger_rate: 10,
  advanced: DEFAULT_ADVANCED,
}
const liveView = new OrthographicView({ flipY: false })

type RGBA = [number, number, number, number]

const VEHICLE_COLORS: Record<VehicleStatus, RGBA> = {
  idle: [45, 212, 191, 235],
  to_pickup: [251, 191, 36, 235],
  on_trip: [52, 211, 153, 235],
  repositioning: [96, 165, 250, 235],
  offline: [148, 163, 184, 40],
}

// 悬停高亮的路线颜色与车辆状态色一致，便于对照
const ROUTE_COLORS: Partial<Record<VehicleStatus, RGBA>> = {
  to_pickup: [251, 191, 36, 255],
  on_trip: [52, 211, 153, 255],
  repositioning: [96, 165, 250, 255],
}
const TRIP_DONE_COLOR: RGBA = [52, 211, 153, 70]
const PASSENGER_COLOR: RGBA = [255, 140, 40, 235]
const HALO_COLOR: RGBA = [255, 255, 255, 235]

const VEHICLE_STATUS_LABEL: Record<VehicleStatus, string> = {
  idle: '空载',
  to_pickup: '接驾中',
  on_trip: '载客中',
  repositioning: '巡游中',
  offline: '离线',
}

const MAP_OPTIONS: { value: StartParams['map']; label: string }[] = [
  { value: 'grid50', label: '50×50 网格' },
]
const ALGORITHM_OPTIONS: { value: StartParams['algorithm']; label: string }[] = [
  { value: 'nearest', label: '最近邻优先' },
  { value: 'regional_hungarian', label: '分区匈牙利' },
  { value: 'greedy_hungarian', label: '匈牙利匹配' },
  { value: 'pooled_insertion', label: '拼车插入' },
]

type HoverKind = 'vehicle' | 'passenger'
type HoverState = { kind: HoverKind; id: number; x: number; y: number }
type PathDatum = { path: [number, number][]; color: RGBA; width: number }
type RingDatum = { position: [number, number]; radius: number; fill: RGBA; line: RGBA; lineWidth: number }

// 后端 id 从 0 起按出现顺序分配，展示编号 +1，即从 1 开始的序号
function sequenceLabel(kind: '车辆' | '乘客', id: number): string {
  return `${kind} #${id + 1}`
}

function isVehicle(object: object): object is Vehicle {
  return 'route_index' in object
}

// 剩余路线：从车辆当前位置沿路线到终点
function remainingPath(vehicle: Vehicle): [number, number][] {
  return [[vehicle.x, vehicle.y], ...vehicle.route.slice(vehicle.route_index + 1)]
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function NumberField(props: {
  label: string
  value: number | ''
  step?: number
  min?: number
  placeholder?: string
  onChange: (value: number) => void
}) {
  return (
    <label className="adv-field">
      <span>{props.label}</span>
      <input
        type="number"
        value={props.value}
        step={props.step ?? 1}
        min={props.min}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(Number(event.target.value))}
      />
    </label>
  )
}

export function LivePage() {
  const [params, setParams] = useState<StartParams>(DEFAULT_PARAMS)
  const [roadnet, setRoadnet] = useState<Roadnet>()
  const [snapshot, setSnapshot] = useState<Snapshot>()
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [error, setError] = useState<string>()
  const [starting, setStarting] = useState(false)
  const [hover, setHover] = useState<HoverState | null>(null)
  const [trend, setTrend] = useState<TrendSample[]>([])
  const [reportsOpen, setReportsOpen] = useState(false)
  const [reports, setReports] = useState<ReportItem[] | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [hotspots, setHotspots] = useState<HotspotOverlay[]>([])
  const [experiment, setExperiment] = useState<ExperimentInfo | null>(null)
  const stageRef = useRef<HTMLElement | null>(null)
  const inFlight = useRef(false)

  const setAdvanced = (updater: (prev: AdvancedParams) => AdvancedParams) =>
    setParams((current) => ({ ...current, advanced: updater(current.advanced ?? DEFAULT_ADVANCED) }))
  const advanced = params.advanced ?? DEFAULT_ADVANCED

  // 快照统一入口：更新统计面板，并把统计指标按 10 秒桶记入曲线。
  // 更新器保持纯函数（StrictMode 会双重调用），桶进位只依赖已有曲线末点。
  const applySnapshot = useCallback((snap: Snapshot) => {
    setSnapshot(snap)
    // 热区与实验时间轴随快照下发；内容不变时保留旧引用，避免热力图每帧重聚合
    setHotspots((prev) => (JSON.stringify(prev) === JSON.stringify(snap.hotspots ?? []) ? prev : snap.hotspots ?? []))
    setExperiment((prev) => (JSON.stringify(prev) === JSON.stringify(snap.experiment ?? null) ? prev : snap.experiment ?? null))
    setTrend((current) => {
      const sample: TrendSample = {
        t: snap.time,
        pickup: snap.stats.avg_pickup_wait_s,
        trip: snap.stats.avg_trip_s,
        onTrip: snap.stats.on_trip_vehicles,
        idle: snap.stats.idle_vehicles,
        waiting: snap.stats.waiting_passengers,
      }
      const last = current[current.length - 1]
      if (last && snap.time < last.t) return [sample]
      if (last && snap.time < last.t + TREND_BUCKET_S) return current
      const next = [...current, sample]
      return next.length > 400 ? next.slice(next.length - 400) : next
    })
  }, [])

  const start = useCallback(async (next: StartParams) => {
    setStarting(true)
    try {
      const result = await startLive(next)
      setRoadnet(result.roadnet)
      applySnapshot(result.snapshot)
      setError(undefined)
      setPlaying(true)
    } catch (cause) {
      setError(describe(cause))
      setPlaying(false)
    } finally {
      setStarting(false)
    }
  }, [applySnapshot])

  // 首次挂载：已有会话则接上（暂停状态）；无会话则停留在待启动状态，由用户手动开始
  const bootstrap = useCallback(async () => {
    try {
      const snap = await getSnapshot(0)
      const net = await getRoadnet()
      setRoadnet(net)
      applySnapshot(snap)
      setError(undefined)
      setPlaying(false)
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        setRoadnet(undefined)
        setError(undefined)
        setPlaying(false)
        return
      }
      setError(describe(cause))
    }
  }, [applySnapshot])

  useEffect(() => { void bootstrap() }, [bootstrap])

  const finished = snapshot?.finished ?? false
  useEffect(() => {
    if (!playing || finished) return
    const { intervalMs, advanceS } = pollPlan(speed)
    const timer = setInterval(async () => {
      if (inFlight.current) return
      inFlight.current = true
      try {
        applySnapshot(await getSnapshot(advanceS))
        setError(undefined)
      } catch (cause) {
        setError(describe(cause))
        setPlaying(false)
      } finally {
        inFlight.current = false
      }
    }, intervalMs)
    return () => clearInterval(timer)
  }, [playing, speed, finished, applySnapshot])

  // 悬停状态只记 id，每帧从最新快照里取，避免拿到过期的位置和路线
  const hoverVehicle = hover?.kind === 'vehicle'
    ? snapshot?.vehicles.find((item) => item.id === hover.id)
    : undefined
  const hoverPassenger = hover?.kind === 'passenger'
    ? snapshot?.passengers.find((item) => item.id === hover.id)
    : undefined
  const hoverPickupVehicle = hoverPassenger?.status === 'assigned' && hoverPassenger.vehicle_id != null
    ? snapshot?.vehicles.find((item) => item.id === hoverPassenger.vehicle_id)
    : undefined

  const handleHover = useCallback((info: PickingInfo) => {
    const object = info.picked && info.object ? info.object : undefined
    if (!object) {
      setHover(null)
      return
    }
    const kind: HoverKind = isVehicle(object) ? 'vehicle' : 'passenger'
    const { id } = object
    setHover((current) => (
      current && current.kind === kind && current.id === id && current.x === info.x && current.y === info.y
        ? current
        : { kind, id, x: info.x, y: info.y }
    ))
  }, [])

  const nodeMap = useMemo(
    () => new Map(roadnet?.nodes.map((node) => [node.id, [node.x, node.y] as [number, number]]) || []),
    [roadnet],
  )
  const roadPaths = useMemo(
    () => roadnet?.edges.map((edge) => ({ path: [nodeMap.get(edge.source)!, nodeMap.get(edge.target)!] })) || [],
    [roadnet, nodeMap],
  )
  const initialViewState = useMemo(() => (roadnet ? fitViewState(roadnet) : undefined), [roadnet])

  const highlightPaths = useMemo<PathDatum[]>(() => {
    const items: PathDatum[] = []
    if (hoverVehicle && hoverVehicle.route.length >= 2) {
      const color = ROUTE_COLORS[hoverVehicle.status]
      if (hoverVehicle.status === 'on_trip') {
        // 载客：完整起终点路线淡色打底，剩余路程按原色高亮
        items.push({ path: hoverVehicle.route, color: TRIP_DONE_COLOR, width: 2 })
        items.push({ path: remainingPath(hoverVehicle), color: ROUTE_COLORS.on_trip!, width: 4 })
      } else if (color) {
        items.push({ path: remainingPath(hoverVehicle), color, width: 4 })
      }
    }
    if (hoverPickupVehicle && hoverPickupVehicle.route.length >= 2) {
      items.push({ path: remainingPath(hoverPickupVehicle), color: ROUTE_COLORS.to_pickup!, width: 4 })
    }
    return items
  }, [hoverVehicle, hoverPickupVehicle])

  const highlightRings = useMemo<RingDatum[]>(() => {
    const items: RingDatum[] = []
    const halo = (position: [number, number]): RingDatum => ({
      position, radius: 40, fill: [255, 255, 255, 0], line: HALO_COLOR, lineWidth: 3,
    })
    if (hoverVehicle) items.push(halo([hoverVehicle.x, hoverVehicle.y]))
    if (hoverPickupVehicle) items.push(halo([hoverPickupVehicle.x, hoverPickupVehicle.y]))
    if (hoverPassenger) {
      items.push({
        position: hoverPassenger.destination,
        radius: 40,
        fill: [255, 140, 40, 30],
        line: [255, 150, 60, 245],
        lineWidth: 3,
      })
    }
    if (hoverVehicle?.status === 'on_trip' && hoverVehicle.route.length >= 2) {
      // 载客路线的起点（上车点）与终点（目的地）标记
      items.push({ position: hoverVehicle.route[0], radius: 12, fill: [52, 211, 153, 255], line: [255, 255, 255, 220], lineWidth: 2 })
      items.push({
        position: hoverVehicle.route[hoverVehicle.route.length - 1],
        radius: 12,
        fill: [255, 140, 40, 255],
        line: [255, 255, 255, 220],
        lineWidth: 2,
      })
    }
    return items
  }, [hoverVehicle, hoverPickupVehicle, hoverPassenger])

  // 热区热力图采样点：每个热区按同心环布点，权重从中心向外衰减
  const hotspotPoints = useMemo(() => {
    const points: Array<{ x: number; y: number; weight: number; kind: string }> = []
    for (const hotspot of hotspots) {
      const rings = 6
      for (let ring = 1; ring <= rings; ring++) {
        const radius = (ring / rings) * hotspot.radius_m
        const count = ring * 12
        for (let k = 0; k < count; k++) {
          const angle = (k / count) * Math.PI * 2 + ring * 0.6
          points.push({
            x: hotspot.x + radius * Math.cos(angle),
            y: hotspot.y + radius * Math.sin(angle),
            weight: 1.4 - ring / rings,
            kind: hotspot.kind,
          })
        }
      }
    }
    return points
  }, [hotspots])

  // 模式切换记录：由实验时间轴推导，跳帧也不丢失边界事件
  const eventLog = useMemo(() => {
    const segments = experiment?.segments ?? []
    if (!segments.length) return []
    const events = [{ time: 0, text: `仿真开始：${segments[0].kind}（需求 ${segments[0].demand_rate} 单/分，供给 ${segments[0].supply}）` }]
    for (let index = 1; index < segments.length; index++) {
      const prev = segments[index - 1]
      const current = segments[index]
      if (current.demand_rate !== prev.demand_rate) {
        events.push({ time: current.start, text: `需求变化：到达率 ${prev.demand_rate} → ${current.demand_rate} 单/分` })
      }
      if (current.supply !== prev.supply) {
        events.push({ time: current.start, text: `供给变化：${prev.supply} → ${current.supply}` })
      }
    }
    const now = snapshot?.time ?? 0
    return events.filter((event) => event.time <= now).reverse()
  }, [experiment, snapshot?.time])

  const layers = useMemo<Layer[]>(() => {
    if (!roadnet || !snapshot) return []
    return [
      new PathLayer({
        id: 'roads', data: roadPaths,
        getPath: (item: { path: [number, number][] }) => item.path,
        getColor: [65, 83, 112, 170], getWidth: 2, widthMinPixels: 1,
      }),
      new PathLayer({
        id: 'hover-routes', data: highlightPaths,
        getPath: (item: PathDatum) => item.path,
        getColor: (item: PathDatum) => item.color,
        getWidth: (item: PathDatum) => item.width,
        widthMinPixels: 1, capRounded: true, jointRounded: true,
      }),
      new ScatterplotLayer({
        id: 'vehicles', data: snapshot.vehicles,
        pickable: true,
        getPosition: (vehicle: Vehicle) => [vehicle.x, vehicle.y],
        getFillColor: (vehicle: Vehicle) =>
          vehicle.status === 'offline' ? [148, 163, 184, 12] : VEHICLE_COLORS[vehicle.status] || VEHICLE_COLORS.offline,
        getLineColor: (vehicle: Vehicle) =>
          vehicle.status === 'offline' ? [148, 163, 184, 25] : [255, 255, 255, 200],
        stroked: true, lineWidthMinPixels: 1.5,
        getRadius: 14, radiusMinPixels: 4,
      }),
      new ScatterplotLayer({
        id: 'passengers', data: snapshot.passengers,
        pickable: true,
        getPosition: (passenger: Passenger) => [passenger.x, passenger.y],
        // 已派单的乘客改为半透明空心圈，点保留到车辆接驾上车为止
        getFillColor: (passenger: Passenger) => (passenger.status === 'pending' ? PASSENGER_COLOR : [255, 140, 40, 45]),
        getLineColor: (passenger: Passenger) => (passenger.status === 'pending' ? [24, 16, 8, 220] : [255, 150, 60, 245]),
        stroked: true,
        getRadius: (passenger: Passenger) => (passenger.status === 'pending' ? 18 : 26),
        lineWidthMinPixels: 2, radiusMinPixels: 5,
      }),
      new HeatmapLayer({
        id: 'hotspot-origin-heat',
        data: hotspotPoints.filter((point) => point.kind === '起点热区'),
        getPosition: (point: { x: number; y: number }) => [point.x, point.y],
        getWeight: (point: { weight: number }) => point.weight,
        radiusPixels: 60,
        intensity: 1,
        threshold: 0.04,
        opacity: 0.45,
        colorRange: [
          [255, 236, 179, 40],
          [254, 178, 76, 120],
          [253, 141, 60, 190],
          [227, 26, 28, 230],
        ],
      }),
      new HeatmapLayer({
        id: 'hotspot-destination-heat',
        data: hotspotPoints.filter((point) => point.kind === '终点热区'),
        getPosition: (point: { x: number; y: number }) => [point.x, point.y],
        getWeight: (point: { weight: number }) => point.weight,
        radiusPixels: 60,
        intensity: 1,
        threshold: 0.04,
        opacity: 0.45,
        colorRange: [
          [209, 229, 240, 40],
          [146, 197, 222, 120],
          [67, 147, 195, 190],
          [21, 71, 132, 230],
        ],
      }),
      new ScatterplotLayer({
        id: 'hover-rings', data: highlightRings,
        getPosition: (item: RingDatum) => item.position,
        getRadius: (item: RingDatum) => item.radius,
        getFillColor: (item: RingDatum) => item.fill,
        getLineColor: (item: RingDatum) => item.line,
        getLineWidth: (item: RingDatum) => item.lineWidth,
        stroked: true, radiusMinPixels: 5, lineWidthMinPixels: 2,
      }),
    ]
  }, [roadnet, snapshot, roadPaths, highlightPaths, highlightRings, hotspotPoints])

  const tooltip = useMemo(() => {
    if (!hover || (!hoverVehicle && !hoverPassenger)) return null
    const width = stageRef.current?.clientWidth ?? 0
    const height = stageRef.current?.clientHeight ?? 0
    return {
      left: Math.min(hover.x + 16, Math.max(width - 190, 0)),
      top: Math.min(hover.y + 16, Math.max(height - 96, 0)),
    }
  }, [hover, hoverVehicle, hoverPassenger])

  // 曲线只展示最近 20 分钟
  const recentTrend = useMemo(() => sliceWindow(trend), [trend])

  const openReports = useCallback(async () => {
    setReportsOpen(true)
    setReports(null)
    try {
      setReports(await getReports())
    } catch (cause) {
      setError(describe(cause))
      setReportsOpen(false)
    }
  }, [])

  const stats = snapshot?.stats
  return (
    <div className="live-page">
      <aside className="live-sidebar">
        <div className="live-title">
          <h1>网约车调度仿真</h1>
        </div>
        <div className="control-group">
          <label>地图</label>
          <div className="segmented">
            {MAP_OPTIONS.map((option) => (
              <button
                key={option.value}
                className={params.map === option.value ? 'active' : ''}
                onClick={() => setParams((current) => ({ ...current, map: option.value }))}
              >{option.label}</button>
            ))}
          </div>
        </div>
        <div className="control-group">
          <label>调度算法</label>
          <div className="segmented">
            {ALGORITHM_OPTIONS.map((option) => (
              <button
                key={option.value}
                className={params.algorithm === option.value ? 'active' : ''}
                onClick={() => setParams((current) => ({ ...current, algorithm: option.value }))}
              >{option.label}</button>
            ))}
          </div>
        </div>
        <div className="slider-row">
          <div className="control-group">
            <label>车辆密度 <b>{params.vehicle_count}</b> 辆</label>
            <input
              aria-label="车辆密度" type="range" min="10" max="500" step="10" value={params.vehicle_count}
              onChange={(event) => setParams((current) => ({ ...current, vehicle_count: Number(event.target.value) }))}
            />
          </div>
          <div className="control-group">
            <label>乘客密度 <b>{params.passenger_rate}</b> 单/分</label>
            <input
              aria-label="乘客密度" type="range" min="10" max="60" step="10" value={params.passenger_rate}
              onChange={(event) => setParams((current) => ({ ...current, passenger_rate: Number(event.target.value) }))}
            />
          </div>
        </div>
        <div className="stat-grid">
          <div className="clock"><span>仿真时钟</span><strong>{snapshot ? formatClock(snapshot.time) : '--:--:--'}</strong></div>
          <div><span>空载车辆</span><strong>{stats?.idle_vehicles ?? '—'}</strong></div>
          <div><span>接驾中车辆</span><strong>{stats?.pickup_vehicles ?? '—'}</strong></div>
          <div><span>载客车辆</span><strong>{stats?.on_trip_vehicles ?? '—'}</strong></div>
          <div><span>等待乘客</span><strong>{stats?.waiting_passengers ?? '—'}</strong></div>
          <div><span>在途乘客</span><strong>{stats?.in_trip_passengers ?? '—'}</strong></div>
          <div><span>累计服务</span><strong>{stats?.served_total ?? '—'}</strong></div>
          <div><span>拼车共享率</span><strong>{stats ? `${(stats.pool_share_rate * 100).toFixed(0)}%` : '—'}</strong></div>
          <div><span>平均绕行</span><strong>{stats?.avg_detour_ratio != null ? `${stats.avg_detour_ratio.toFixed(2)}×` : '—'}</strong></div>
        </div>
        <StatChart
          points={recentTrend}
          series={AVERAGE_SERIES}
          current={stats ? [stats.avg_pickup_wait_s, stats.avg_trip_s] : null}
          digits={1}
          suffix="s"
        />
        <StatChart
          points={recentTrend}
          series={COUNT_SERIES}
          current={stats ? [stats.on_trip_vehicles, stats.idle_vehicles, stats.waiting_passengers] : null}
          digits={0}
          suffix=""
        />
        {finished && <p className="finished-note">仿真已结束</p>}
        <div className="speed-bar">
          <button className="play-button" onClick={() => setPlaying((value) => !value)} disabled={!snapshot || finished}>
            {playing ? '暂停' : '播放'}
          </button>
          <div className="segmented">
            {SPEED_GEARS.map((gear) => (
              <button key={gear} className={speed === gear ? 'active' : ''} onClick={() => setSpeed(gear)}>{gear}×</button>
            ))}
          </div>
        </div>
        <div className="action-row">
          <button className="secondary-button" disabled={starting} onClick={() => void start(params)}>
            {starting ? '启动中…' : '重新开始'}
          </button>
          <button className="secondary-button" onClick={() => setAdvancedOpen(true)}>高级参数</button>
          <button className="secondary-button" onClick={() => void openReports()}>仿真报告</button>
        </div>
      </aside>
      <section className="map-stage" ref={stageRef} onMouseLeave={() => setHover(null)}>
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => void bootstrap()}>重试</button>
          </div>
        )}
        {roadnet && initialViewState ? (
          <DeckGL key={roadnet.checksum} views={liveView} initialViewState={initialViewState} controller layers={layers} onHover={handleHover} />
        ) : (
          <div className="state-panel">
            {error ? (
              <>
                <div className="loader" />
                <p>连接后端失败</p>
              </>
            ) : (
              <>
                <p className="state-title">仿真未启动</p>
                <p className="state-sub">点击左下角「重新开始」开始仿真</p>
              </>
            )}
          </div>
        )}
        {eventLog.length > 0 && (
          <div className="event-log">
            <p className="event-log-latest">
              <span className="event-time">{formatClock(eventLog[0].time)}</span>
              {eventLog[0].text}
            </p>
            <div className="event-log-history">
              {eventLog.map((event) => (
                <p key={`${event.time}-${event.text}`}>
                  <span className="event-time">{formatClock(event.time)}</span>
                  {event.text}
                </p>
              ))}
            </div>
          </div>
        )}
        {tooltip && (
          <div className="map-tooltip" style={{ left: tooltip.left, top: tooltip.top }}>
            {hoverVehicle && (
              <>
                <p className="tooltip-title">{sequenceLabel('车辆', hoverVehicle.id)}</p>
                <p>{VEHICLE_STATUS_LABEL[hoverVehicle.status]}</p>
                {hoverVehicle.order_id != null && <p>{sequenceLabel('乘客', hoverVehicle.order_id)}</p>}
              </>
            )}
            {hoverPassenger && (
              <>
                <p className="tooltip-title">{sequenceLabel('乘客', hoverPassenger.id)}</p>
                <p>已等待 {Math.round(hoverPassenger.wait_s)} 秒</p>
                <p>
                  {hoverPassenger.status === 'assigned' && hoverPassenger.vehicle_id != null
                    ? `${sequenceLabel('车辆', hoverPassenger.vehicle_id)} 接驾中`
                    : '等待派单'}
                </p>
              </>
            )}
          </div>
        )}
      </section>
      {reportsOpen && (
        <div className="modal-overlay" onClick={() => setReportsOpen(false)}>
          <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
            <header className="modal-header">
              <span>仿真报告</span>
              <button className="modal-close" aria-label="关闭" onClick={() => setReportsOpen(false)}>×</button>
            </header>
            {reports === null ? (
              <p className="modal-hint">加载中…</p>
            ) : reports.length === 0 ? (
              <p className="modal-hint">暂无历史报告，完成一次仿真后自动生成</p>
            ) : (
              <ul className="report-list">
                {reports.map((item) => (
                  <li key={item.run_id} className="report-item">
                    <div className="report-info">
                      <p className="report-title">
                        {item.created_at} · {item.map} · {item.algorithm}
                        {item.status !== 'finished' && <em className="report-badge">提前停止</em>}
                      </p>
                      <p className="report-sub">
                        {item.vehicle_count} 辆 · {item.passenger_rate} 单/分 · 订单 {item.served}/{item.total} ·
                        {' '}平均接驾 {item.avg_pickup_wait_s.toFixed(1)}s · 平均乘车 {item.avg_trip_s.toFixed(1)}s
                      </p>
                    </div>
                    <a className="report-open" href={reportUrl(item.run_id)} target="_blank" rel="noreferrer">查看</a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      {advancedOpen && (
        <div className="modal-overlay" onClick={() => setAdvancedOpen(false)}>
          <div className="modal-panel adv-panel" onClick={(event) => event.stopPropagation()}>
            <header className="modal-header">
              <span>高级参数</span>
              <button className="modal-close" aria-label="关闭" onClick={() => setAdvancedOpen(false)}>×</button>
            </header>
            <div className="adv-body">
              <section className="adv-group">
                <h3>订单取消</h3>
                <div className="adv-grid">
                  <NumberField
                    label="取消等待时长（秒）" value={advanced.cancel_after_s} min={10}
                    onChange={(value) => setAdvanced((prev) => ({ ...prev, cancel_after_s: value }))}
                  />
                </div>
              </section>
              <section className="adv-group">
                <h3>时间与空间分流实验</h3>
                <button className="primary-action" onClick={() => setAdvanced((prev) => ({ ...prev, ...randomDiversion() }))}>
                  一键开启时空分流
                </button>
                <p className="adv-hint">
                  仿真自动延长到 90 分钟（预热 10 分 + 3 个 20 分钟片 + 2 个 10 分钟缓冲）：
                  片1 供给过剩、片2 高峰匹配、片3 双重挤压，同时开启起/终点热区与空车巡游引导。
                </p>
                <label className="adv-check">
                  <input
                    type="checkbox" checked={advanced.time_diversion.enabled}
                    onChange={(event) => setAdvanced((prev) => ({ ...prev, time_diversion: { ...prev.time_diversion, enabled: event.target.checked } }))}
                  />
                  启用时间分流（三个 20 分钟片，供需档位逐片可调）
                </label>
                {advanced.time_diversion.enabled && (
                  <div className="slice-table">
                    {advanced.time_diversion.slices.map((slice, index) => (
                      <div key={index} className="slice-row">
                        <span className="slice-name">片 {index + 1}</span>
                        <label>
                          需求
                          <select
                            value={slice.demand}
                            onChange={(event) => setAdvanced((prev) => ({
                              ...prev,
                              time_diversion: {
                                ...prev.time_diversion,
                                slices: prev.time_diversion.slices.map((item, i) =>
                                  i === index ? { ...item, demand: event.target.value as SliceLevel } : item),
                              },
                            }))}
                          >
                            {(Object.keys(DEMAND_LEVEL_LABEL) as SliceLevel[]).map((level) => (
                              <option key={level} value={level}>{DEMAND_LEVEL_LABEL[level]}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          供给
                          <select
                            value={slice.supply}
                            onChange={(event) => setAdvanced((prev) => ({
                              ...prev,
                              time_diversion: {
                                ...prev.time_diversion,
                                slices: prev.time_diversion.slices.map((item, i) =>
                                  i === index ? { ...item, supply: event.target.value as SliceLevel } : item),
                              },
                            }))}
                          >
                            {(Object.keys(SUPPLY_LEVEL_LABEL) as SliceLevel[]).map((level) => (
                              <option key={level} value={level}>{SUPPLY_LEVEL_LABEL[level]}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                <p className="adv-hint">
                  时间轴：0–10 分预热 → 片1 10–30 分 → 缓冲 30–40 分 → 片2 40–60 分 → 缓冲 60–70 分 → 片3 70–90 分。
                  预热与缓冲使用基准供需，不计入统计；高峰片的供给波次会提前在缓冲段上线就位。
                </p>
              </section>
              <section className="adv-group">
                <h3>空间分流（需求热点）</h3>
                <label className="adv-check">
                  <input
                    type="checkbox" checked={advanced.hotspot.origin}
                    onChange={(event) => setAdvanced((prev) => ({ ...prev, hotspot: { ...prev.hotspot, origin: event.target.checked } }))}
                  />
                  起点热区（订单密集上车点，位置与半径随机生成）
                </label>
                <label className="adv-check">
                  <input
                    type="checkbox" checked={advanced.hotspot.destination}
                    onChange={(event) => setAdvanced((prev) => ({ ...prev, hotspot: { ...prev.hotspot, destination: event.target.checked } }))}
                  />
                  终点热区（订单密集目的地，位置与半径随机生成）
                </label>
                <label className="adv-field">
                  <span>热区强度（订单落入热区的比例）</span>
                  <select
                    value={advanced.hotspot.intensity}
                    onChange={(event) => setAdvanced((prev) => ({ ...prev, hotspot: { ...prev.hotspot, intensity: event.target.value as IntensityLevel } }))}
                  >
                    {(Object.keys(INTENSITY_LABEL) as IntensityLevel[]).map((level) => (
                      <option key={level} value={level}>{INTENSITY_LABEL[level]}</option>
                    ))}
                  </select>
                </label>
                {(advanced.hotspot.origin || advanced.hotspot.destination) && (
                  <label className="adv-check">
                    <input
                      type="checkbox" checked={advanced.hotspot.guide_idle}
                      onChange={(event) => setAdvanced((prev) => ({ ...prev, hotspot: { ...prev.hotspot, guide_idle: event.target.checked } }))}
                    />
                    空车巡游受热区引导（起点热区吸引、终点热区排斥，作用随距离衰减；巡游保持随机游走）
                  </label>
                )}
              </section>
              <section className="adv-group">
                <h3>计价</h3>
                <div className="adv-grid">
                  <NumberField
                    label="起步价（元）" value={advanced.pricing.base_fare} step={0.5} min={0}
                    onChange={(value) => setAdvanced((prev) => ({ ...prev, pricing: { ...prev.pricing, base_fare: value } }))}
                  />
                  <NumberField
                    label="起步里程（km）" value={advanced.pricing.base_distance_km} step={0.5} min={0}
                    onChange={(value) => setAdvanced((prev) => ({ ...prev, pricing: { ...prev.pricing, base_distance_km: value } }))}
                  />
                  <NumberField
                    label="里程单价（元/km）" value={advanced.pricing.per_km} step={0.5} min={0}
                    onChange={(value) => setAdvanced((prev) => ({ ...prev, pricing: { ...prev.pricing, per_km: value } }))}
                  />
                  <NumberField
                    label="低速时长费（元/分）" value={advanced.pricing.per_minute_low_speed} step={0.5} min={0}
                    onChange={(value) => setAdvanced((prev) => ({ ...prev, pricing: { ...prev.pricing, per_minute_low_speed: value } }))}
                  />
                  <NumberField
                    label="低速阈值（m/s）" value={advanced.pricing.low_speed_threshold_mps} step={0.5} min={0}
                    onChange={(value) => setAdvanced((prev) => ({ ...prev, pricing: { ...prev.pricing, low_speed_threshold_mps: value } }))}
                  />
                  <NumberField
                    label="平台分成" value={advanced.pricing.platform_share} step={0.05} min={0}
                    onChange={(value) => setAdvanced((prev) => ({ ...prev, pricing: { ...prev.pricing, platform_share: value } }))}
                  />
                </div>
              </section>
            </div>
            <footer className="adv-footer">
              <button className="restart-button" onClick={() => setAdvancedOpen(false)}>完成</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  )
}

export default LivePage
